import React, { useState } from 'react';
 
// ─── Color tokens (same as Home.tsx) ─────────────────────────────────────────
const C = {
    primary:          '#6a37d4',
    primaryContainer: '#ede8ff',
    surface:          '#f3f6ff',
    surfaceLow:       '#eaf1ff',
    surfaceContainer: '#dce9fd',
    onBackground:     '#252f3c',
    onSurface:        '#252f3c',
    onSurfaceVariant: '#525c6a',
    outlineVariant:   '#a4aebe',
};
 
interface FooterProps {
    onNavigateFeaturedStories?: () => void;
}
 
const Footer: React.FC<FooterProps> = ({ onNavigateFeaturedStories }) => {
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
 
    React.useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);
 
    const navGroups = [
        {
            heading: 'Explore',
            links: [
                { label: 'Featured Stories', href: '/featured-stories' },
                { label: 'Writing Prompts',  href: '/prompts' },
                { label: 'Hall of Fame',     href: '/hall-of-fame' },
            ],
        },
        {
            heading: 'Info',
            links: [
                { label: 'Free Resources',       href: '/resources' },
                { label: 'For Parents',          href: '#parents' },
                { label: 'For Teachers',         href: '/for-teachers' },
                { label: 'Community Guidelines', href: '/community-guidelines' },
                { label: 'Privacy Policy',       href: '/privacy-policy' },
            ],
        },
    ];
 
    const socialIcons = [
        { label: 'Instagram', emoji: '📷', href: 'https://instagram.com/scriblishofficial' },
       
    ];
 
    return (
        <footer style={{
            background: C.surfaceLow,
            borderRadius: '3rem 3rem 0 0',
            marginTop: '0',
            fontFamily: "'Plus Jakarta Sans', sans-serif",
        }}>
            {/* Top strip */}
            <div style={{
                maxWidth: '1200px',
                margin: '0 auto',
                padding: isMobile ? '48px 24px 36px' : '64px 48px 40px',
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : '1.6fr 1fr 1fr',
                gap: isMobile ? '40px' : '48px',
                alignItems: 'flex-start',
            }}>
 
                {/* Brand column */}
                <div>
                    {/* Logo */}
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        marginBottom: '16px',
                    }}>
                        <span style={{ fontSize: '1.6rem' }}>✏️</span>
                        <span style={{
                            fontFamily: "'Epilogue', sans-serif",
                            fontWeight: '900',
                            fontSize: '1.5rem',
                            color: C.primary,
                            fontStyle: 'italic',
                            letterSpacing: '-0.5px',
                        }}>
                            Scriblish
                        </span>
                    </div>
 
                    <p style={{
                        color: C.onSurfaceVariant,
                        fontSize: '0.9rem',
                        lineHeight: '1.65',
                        margin: '0 0 24px 0',
                        maxWidth: '260px',
                    }}>
                        A safe, creative writing studio for young storytellers aged 10–13. Plan it. Write it. Share it.
                    </p>
 
                    {/* Social icons */}
                    <div style={{ display: 'flex', gap: '10px' }}>
                        {socialIcons.map(({ label, emoji, href }) => (
                            <a
                                key={label}
                                href={href}
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label={label}
                                style={{
                                    width: '40px',
                                    height: '40px',
                                    borderRadius: '50%',
                                    background: C.surfaceContainer,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '1.1rem',
                                    textDecoration: 'none',
                                    transition: 'background 0.2s, transform 0.2s',
                                    border: `1px solid ${C.outlineVariant}30`,
                                }}
                                onMouseEnter={e => {
                                    (e.currentTarget as HTMLElement).style.background = C.primaryContainer;
                                    (e.currentTarget as HTMLElement).style.transform = 'translateY(-2px)';
                                }}
                                onMouseLeave={e => {
                                    (e.currentTarget as HTMLElement).style.background = C.surfaceContainer;
                                    (e.currentTarget as HTMLElement).style.transform = 'translateY(0)';
                                }}
                            >
                                {emoji}
                            </a>
                        ))}
                    </div>
                </div>
 
                {/* Nav link columns */}
                {navGroups.map(group => (
                    <div key={group.heading}>
                        <p style={{
                            fontFamily: "'Epilogue', sans-serif",
                            fontWeight: '800',
                            fontSize: '0.8rem',
                            color: C.onBackground,
                            textTransform: 'uppercase',
                            letterSpacing: '1.2px',
                            margin: '0 0 16px 0',
                        }}>
                            {group.heading}
                        </p>
                        <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: '10px' }}>
                            {group.links.map(({ label, href }) => (
                                <li key={label}>
                                    <a
                                        href={href}
                                        style={{
                                            color: C.onSurfaceVariant,
                                            textDecoration: 'none',
                                            fontSize: '0.9rem',
                                            fontWeight: '500',
                                            transition: 'color 0.15s',
                                            display: 'inline-block',
                                        }}
                                        onMouseEnter={e => { (e.currentTarget as HTMLElement).style.color = C.primary; }}
                                        onMouseLeave={e => { (e.currentTarget as HTMLElement).style.color = C.onSurfaceVariant; }}
                                    >
                                        {label}
                                    </a>
                                </li>
                            ))}
                        </ul>
                    </div>
                ))}
            </div>
 
            {/* Divider */}
            <div style={{
                maxWidth: '1200px',
                margin: '0 auto',
                padding: '0 48px',
            }}>
                <div style={{
                    height: '1px',
                    background: C.outlineVariant,
                    opacity: 0.3,
                }} />
            </div>
 
            {/* Bottom bar */}
            <div style={{
                maxWidth: '1200px',
                margin: '0 auto',
                padding: isMobile ? '20px 24px 32px' : '20px 48px 36px',
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                alignItems: isMobile ? 'flex-start' : 'center',
                justifyContent: 'space-between',
                gap: '12px',
            }}>
                <p style={{
                    color: C.onSurfaceVariant,
                    fontSize: '0.82rem',
                    margin: 0,
                }}>
                    © {new Date().getFullYear()} Scriblish. Built for young storytellers. 🌟
                </p>
 
                <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
                    {[
                        { label: 'Terms of Service',     href: '/terms' },
                        { label: 'Privacy Policy',       href: '/privacy-policy' },
                        { label: 'Community Guidelines', href: '/community-guidelines' },
                        { label: 'Safety Center',        href: '/safety' },
                    ].map(({ label, href }) => (
                        <a
                            key={label}
                            href={href}
                            style={{
                                color: C.onSurfaceVariant,
                                fontSize: '0.82rem',
                                textDecoration: 'none',
                                transition: 'color 0.15s',
                            }}
                            onMouseEnter={e => { (e.currentTarget as HTMLElement).style.color = C.primary; }}
                            onMouseLeave={e => { (e.currentTarget as HTMLElement).style.color = C.onSurfaceVariant; }}
                        >
                            {label}
                        </a>
                    ))}
                </div>
            </div>
        </footer>
    );
};
 
export default Footer;